import { useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import type { ScriptableContext } from 'chart.js';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './../../scss/components/bar-chart.style.scss';
import { useAnalysis } from '../../hooks/useAnalysis';
import { useAppDispatch } from '../../hooks/useRedux';
import { fetchAnalysisAsync } from '../../features/analysis/analysis.slice';
import { useTranslation } from 'react-i18next';
import { useUserDashboard } from '../../hooks/useUserDashboard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Resolve a design token (e.g. `--ds-primary` => "255 42% 51%") into an
 * `hsl()` color string so the chart canvas stays in sync with the SCSS
 * design system instead of hardcoding colors.
 */
const TOKEN_FALLBACK: Record<string, string> = {
  '--ds-primary': '255 42% 51%',
  '--ds-primary-700': '257 39% 30%',
  '--ds-primary-900': '257 45% 18%',
  '--ds-coral': '6 84% 71%',
  '--ds-peach': '22 92% 70%',
  '--ds-pink': '322 80% 62%',
  '--ds-primary-light': '271 91% 65%',
  '--ds-foreground': '222 25% 13%',
  '--ds-muted-foreground': '215 13% 45%',
  '--ds-border': '228 24% 93%',
};

const token = (name: string, alpha = 1): string => {
  let raw = '';
  if (typeof window !== 'undefined') {
    raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }
  if (!raw) raw = TOKEN_FALLBACK[name] || '215 13% 45%';
  return `hsl(${raw} / ${alpha})`;
};

type GradientStop = { token: string; at: number; alpha?: number };

/** Vertical multi-stop gradient fill for a bar, derived from design tokens. */
const barGradient =
  (stops: GradientStop[]) =>
  (ctx: ScriptableContext<'bar'>): string | CanvasGradient => {
    const { chart } = ctx;
    const { ctx: canvas, chartArea } = chart;
    if (!chartArea) return token(stops[0].token, stops[0].alpha ?? 0.9);
    const g = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    stops.forEach((s) => g.addColorStop(s.at, token(s.token, s.alpha ?? 1)));
    return g;
  };

// Current year: theme violets (#7c3aed → #a855f7). Previous year: warm coral → peach.
const gradientCurrent: GradientStop[] = [
  { token: '--ds-primary', at: 0, alpha: 1 },
  { token: '--ds-primary-light', at: 0.7, alpha: 0.95 },
  { token: '--ds-primary-light', at: 1, alpha: 0.78 },
];
const gradientPrevious: GradientStop[] = [
  { token: '--ds-coral', at: 0, alpha: 0.95 },
  { token: '--ds-peach', at: 1, alpha: 0.65 },
];

const options = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 1100,
    easing: 'easeOutQuart' as const,
    delay: (ctx: any) =>
      ctx.type === 'data' && ctx.mode === 'default'
        ? ctx.dataIndex * 55 + ctx.datasetIndex * 30
        : 0,
  },
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        font: {
          family: 'Plus Jakarta Sans',
          size: 12,
          weight: 600,
        },
        color: token('--ds-foreground'),
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 8,
        boxHeight: 8,
        padding: 18,
      },
    },
    title: {
      display: false,
    },
    tooltip: {
      backgroundColor: token('--ds-primary-900', 0.82),
      titleColor: '#ffffff',
      bodyColor: 'rgba(255,255,255,0.92)',
      borderColor: 'rgba(255,255,255,0.18)',
      borderWidth: 1,
      cornerRadius: 14,
      padding: 14,
      titleFont: { family: 'Plus Jakarta Sans', size: 13, weight: 700 },
      bodyFont: { family: 'Plus Jakarta Sans', size: 13 },
      boxPadding: 6,
      usePointStyle: true,
      displayColors: true,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
        drawBorder: false,
      },
      ticks: {
        font: {
          family: 'Plus Jakarta Sans',
          size: 11,
        },
        color: token('--ds-muted-foreground'),
      },
    },
    y: {
      beginAtZero: true,
      border: { display: false },
      grid: {
        color: token('--ds-border', 0.7),
        drawBorder: false,
        lineWidth: 1,
      },
      ticks: {
        font: {
          family: 'Plus Jakarta Sans',
          size: 11,
        },
        color: token('--ds-muted-foreground'),
      },
    },
  },
  elements: {
    bar: {
      borderRadius: 8,
      borderSkipped: false as const,
    },
  },
};



export const BarChart = () => {

  const { analysisData, loading, error } = useAnalysis();
  const {
    dashboardData,
  } = useUserDashboard();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(fetchAnalysisAsync());
  }, []);

  const currentYear = new Date().getFullYear();
  const year1 = currentYear - 1;
  const year2 = currentYear;
  const mappedData = {
    labels: analysisData?.response.data?.map((item) => t(item.month)),
    datasets: [
      {
        label: `${year1}`,
        data: analysisData?.response.data?.map((item) => item.year1Amount),
        borderColor: 'transparent',
        backgroundColor: barGradient(gradientPrevious),
        hoverBackgroundColor: barGradient(
          gradientPrevious.map((s) => ({ ...s, alpha: Math.min((s.alpha ?? 1) + 0.18, 1) })),
        ),
        borderRadius: 10,
        borderSkipped: false,
        maxBarThickness: 30,
        categoryPercentage: 0.68,
        barPercentage: 0.9,
      },
      {
        label: `${year2}`,
        data: analysisData?.response.data?.map((item) => item.year2Amount),
        borderColor: 'transparent',
        backgroundColor: barGradient(gradientCurrent),
        hoverBackgroundColor: barGradient(
          gradientCurrent.map((s) => ({ ...s, alpha: Math.min((s.alpha ?? 1) + 0.12, 1) })),
        ),
        borderRadius: 10,
        borderSkipped: false,
        maxBarThickness: 30,
        categoryPercentage: 0.68,
        barPercentage: 0.9,
      },
    ],
  };
  return (
    <div className="bar-chart-container">
      <div className='d-flex justify-content-between align-items-center overflow-y-scroll '>
        <div>
          <h2 className='mb-0 bar-chart-heading'>{t('INVOICE_ANALYSIS')}</h2>
          <p className="bar-chart-subtitle">{t('INCOME_TRENDS_WILL_SHOW_ONCE_YOU_START_INVOICING')}</p>
        </div>
      </div>
      {Number(dashboardData?.response.length) === 0 ? (
        <div className="bar-chart__empty">
          <div className="bar-chart__empty-icon">
            <svg width="160" height="160" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="80" cy="80" r="80" fill="#EDEBF5" />
              <rect x="50" y="84" width="15" height="28" rx="4" fill="#C4B5FD" />
              <rect x="72" y="68" width="15" height="44" rx="4" fill="#A855F7" />
              <rect x="94" y="50" width="15" height="62" rx="4" fill="#7C3AED" />
              <line x1="46" y1="113.5" x2="114" y2="113.5" stroke="#7C3AED" strokeOpacity="0.4" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
          <p className="bar-chart__empty-title">{t('NO_CHART_DATA_TITLE')}</p>
        </div>
      ) : (
        <Bar data={mappedData} options={options} />
      )}
    </div>
  );
};
